import { useState } from 'react';
import { Copy, RotateCcw, KeyRound } from 'lucide-react';
import { Alert, Badge, Button, Input, Select, Switch } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';

/**
 * Environment settings, split by permission.
 *
 * Everything above the divider is `mockapi.author`: how the mock behaves when it
 * answers. Everything below is `mockapi.configure`, because proxy/record mode, the
 * upstream URL and the stored upstream credentials make REAL outbound calls with REAL
 * credentials — a strictly larger privilege than editing a canned response body, and
 * one the server enforces on a separate endpoint.
 */
const errText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;
const textareaClass = 'w-full border rounded-md px-2 py-1.5 text-sm font-mono bg-[var(--app-surface)]';

function CopyRow({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <div className="text-sm">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-xs px-2 py-1.5 rounded border border-[var(--app-border)] truncate" title={value}>{value}</code>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            // Clipboard access can be refused (insecure context, permissions) — a failed
            // copy must not look like a broken button, so the code stays selectable above.
            navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }).catch(() => {});
          }}
        >
          <Copy size={13} /> {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {hint && <div className="text-xs text-secondary-500">{hint}</div>}
    </div>
  );
}

/** Proxy/record configuration — its own component because it is its own permission:
 *  these settings spend real outbound calls with real credentials. */
function ConfigureSection({ env, call, busy, fetchApi, setErr, upstreamHeaders, setUpstreamHeaders, newAccessKey, setNewAccessKey }) {
  return (
      <Can
        module="mockapi"
        action="configure"
        fallback={(
          <div className="text-sm text-secondary-500">
            Proxy and record mode, the upstream URL and the access key need the
            <code className="mx-1 font-mono text-xs">mockapi.configure</code> permission.
          </div>
        )}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-56">
              <Select
                label="Mode"
                options={[
                  { label: 'Mock — answer from the spec only', value: 'mock' },
                  { label: 'Proxy — forward what the spec misses', value: 'proxy' },
                  { label: 'Record — forward and capture it', value: 'record' },
                ]}
                value={env.mode || 'mock'}
                onChange={(v) => call('mockEnvironmentSettings', { id: env.id, mode: v })}
              />
            </div>
            {env.mode !== 'mock' && <Badge variant="warning" size="sm">makes real outbound calls</Badge>}
          </div>

          <Input
            label="Upstream URL"
            defaultValue={env.upstream_url || ''}
            placeholder="https://api.stripe.com"
            onBlur={(e) => call('mockEnvironmentSettings', { id: env.id, upstreamUrl: e.target.value })}
          />

          <label className="block space-y-1">
            <span className="text-sm">
              Upstream headers {env.has_upstream_headers && <Badge variant="success" size="sm">stored</Badge>}
            </span>
            <textarea
              className={textareaClass}
              rows={3}
              value={upstreamHeaders}
              onChange={(e) => setUpstreamHeaders(e.target.value)}
              placeholder='{"Authorization":"Bearer sk_test_…"}'
            />
            <span className="block text-xs text-secondary-500">
              Encrypted at rest and never shown again — only whether one is set. Sent on every
              forwarded request; stripped from anything recorded.
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                let parsed = null;
                try {
                  parsed = upstreamHeaders.trim() ? JSON.parse(upstreamHeaders) : {};
                } catch {
                  setErr('Upstream headers must be a JSON object.');
                  return;
                }
                call('mockEnvironmentSettings', { id: env.id, upstreamHeaders: parsed })
                  .then(() => setUpstreamHeaders(''));
              }}
            >
              {upstreamHeaders.trim() ? 'Save headers' : 'Clear stored headers'}
            </Button>
          </label>

          <div className="flex items-center gap-3">
            <Switch
              label="Allow a private/internal upstream"
              checked={!!env.allow_private_upstream}
              onChange={(v) => call('mockEnvironmentSettings', { id: env.id, allowPrivateUpstream: !!v })}
            />
            <span className="text-xs text-secondary-500">
              Off by default. Turning it on lets this environment reach your own network — a
              deliberate, audited choice, and nothing an agent can make for you.
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => call('mockEnvironmentRotateKey', env.id)}
            >
              <RotateCcw size={13} /> Rotate public URL
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => fetchApi('mockEnvironmentAccessKey', env.id)
                .then((res) => setNewAccessKey(res?.access_key || ''))
                .catch((ex) => setErr(errText(ex)))}
            >
              <KeyRound size={13} /> {env.has_access_key ? 'Replace access key' : 'Require an access key'}
            </Button>
            <span className="text-xs text-secondary-500">
              Rotating breaks every caller using the old URL — which is what makes it the fix for a
              leaked one.
            </span>
          </div>

          {newAccessKey && (
            <Alert variant="warning" title="Copy this now — it is not shown again">
              <code className="font-mono text-xs break-all">{newAccessKey}</code>
              <div className="text-xs mt-1">Send it as the <code className="font-mono">X-Mock-Key</code> header.</div>
            </Alert>
          )}
        </div>
      </Can>
  );
}

export default function EnvSettingsPanel({ environment, onChanged }) {
  const { fetchApi } = useAppApi();
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [newAccessKey, setNewAccessKey] = useState('');
  const [upstreamHeaders, setUpstreamHeaders] = useState('');

  const call = async (key, data) => {
    setBusy(true);
    setErr('');
    try {
      const updated = await fetchApi(key, data);
      if (updated && updated.id) onChanged(updated);
      return updated;
    } catch (ex) {
      setErr(errText(ex));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const env = environment;

  return (
    <div className="space-y-4">
      {err && <Alert variant="error" title="Could not save">{err}</Alert>}

      <CopyRow
        label="Public URL"
        value={env.serve_url}
        hint="Anything can call this — Postman, curl, a staging service. The key is unguessable, so the URL itself is the credential: treat it like one."
      />
      <CopyRow
        label="In-platform URL"
        value={env.mock_url}
        hint="Use this in a workflow HTTP Request node or the send_http tool. It is answered in-process, so it works with no egress and never leaves this machine."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input
          label="Default latency (ms)"
          type="number"
          defaultValue={env.default_latency_ms || 0}
          onBlur={(e) => call('mockEnvironmentUpdate', { id: env.id, defaultLatencyMs: Number(e.target.value) || 0 })}
        />
        <Input
          label="Failure rate (0–1)"
          type="number"
          step="0.05"
          defaultValue={env.failure_rate ?? ''}
          onBlur={(e) => call('mockEnvironmentUpdate', { id: env.id, failureRate: e.target.value === '' ? null : Number(e.target.value) })}
        />
        <Input
          label="Fallback status"
          type="number"
          defaultValue={env.fallback_status || 404}
          onBlur={(e) => call('mockEnvironmentUpdate', { id: env.id, fallbackStatus: Number(e.target.value) || 404 })}
        />
      </div>
      <div className="text-xs text-secondary-500">
        Failure rate injects a 503 with Retry-After on a MATCHED request, for exercising a
        caller&apos;s retry path. It is never applied to an unmatched one, and Try it always
        ignores it.
      </div>

      <hr className="border-[var(--app-border)]" />

      <ConfigureSection
        env={env}
        call={call}
        busy={busy}
        fetchApi={fetchApi}
        setErr={setErr}
        upstreamHeaders={upstreamHeaders}
        setUpstreamHeaders={setUpstreamHeaders}
        newAccessKey={newAccessKey}
        setNewAccessKey={setNewAccessKey}
      />
    </div>
  );
}
